import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import SuccessMessageContext, { SuccessMessageContextType } from './SuccessMessageContext';
import { useClientStatusContext } from '../../ClientStatusContext';

interface Props {
  children: ReactNode;
}

const SuccessMessageContextProvider = ({ children }: Props): ReactElement => {
  const [messages, setMessages] = useState<Array<string>>([]);
  const [displayed, setDisplayed] = useState(false);
  const currentLocation = useLocation();
  const { clearStatus } = useClientStatusContext();

  useLayoutEffect(() => {
    setDisplayed(true);
  }, [currentLocation.pathname, currentLocation.search]);

  useLayoutEffect(() => {
    if (displayed) {
      setMessages([]);
    }
  }, [displayed]);

  const displaySuccess = useCallback((text: string): void => {
    if (clearStatus) {
      clearStatus();
    }
    setMessages([text]);
    setDisplayed(false);
  }, [clearStatus]);

  const clearMessages = (): void => {
    setMessages([]);
  };

  const clearMessage = useCallback((message: string): void => {
    setMessages(prevState => prevState.filter(currentMessage => currentMessage !== message));
  }, []);

  const value: SuccessMessageContextType = useMemo(() => ({
    messages,
    displaySuccess: text => displaySuccess(text),
    clearMessages,
    clearMessage: message => clearMessage(message),
  }), [clearMessage, displaySuccess, messages]);

  return (
    <SuccessMessageContext.Provider value={value}>
      {children}
    </SuccessMessageContext.Provider>
  );
};

export default SuccessMessageContextProvider;
